import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { ensureListing, ensureUser, userDisplayNameAsString } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { getCategoryNames } from '../../util/fieldHelpers';
import { AspectRatioWrapper, ResponsiveImage, UserReviewsSummary } from '../../components';
import { useUserReviews } from '../../hooks/useUserReviews';
import { useProviderListings } from '../../hooks/useProviderListings';
import { REVIEW_TYPE_OF_PROVIDER } from '../../util/types';
import NamedLink from '../NamedLink/NamedLink';
import css from './ProviderCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

/**
 * ProviderCard component
 * Displays a single provider with their image, name, reviews, and list of services
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className - Custom class that extends the default class
 * @param {string?} props.rootClassName - Custom class that extends the default class
 * @param {Object} props.provider - The provider user object
 * @param {Array} props.listings - Array of listings for this provider
 * @param {string?} props.renderSizes - For img/srcset
 * @returns {JSX.Element}
 */
export const ProviderCard = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const {
    className,
    rootClassName,
    provider,
    listings = [],
    renderSizes,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const providerUser = ensureUser(provider);
  const providerId = providerUser.id?.uuid;
  const providerName = userDisplayNameAsString(providerUser, '');
  
  // Get provider image - prefer profileImage, fallback to first listing image
  const profileImage = providerUser.profileImage;
  const firstListing = listings.length > 0 ? ensureListing(listings[0]) : null;
  const firstListingImage = firstListing?.images?.[0] || null;
  const displayImage = profileImage || firstListingImage;

  // Fetch provider reviews (staggered on search results to avoid API burst)
  const deferFetchMs = props.deferFetchMs ?? 0;
  const { reviews: userReviews, loading: reviewsLoading } = useUserReviews(
    providerId,
    REVIEW_TYPE_OF_PROVIDER,
    deferFetchMs
  );

  // Fetch ALL listings for this provider (not just filtered ones) to show all categories
  const { listings: allProviderListings } = useProviderListings(providerId, config, deferFetchMs);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const variants = displayImage?.attributes?.variants
    ? Object.keys(displayImage.attributes.variants).filter(k =>
        k.startsWith(variantPrefix)
      )
    : [];

  // Get unique categories from ALL provider listings (not just filtered ones)
  const categoryConfiguration = config.categoryConfiguration;
  const categoryPrefix = categoryConfiguration?.key || 'categoryLevel';
  const categories = categoryConfiguration?.categories || [];

  const serviceCategories = useMemo(() => {
    if (!categories || categories.length === 0) {
      return [];
    }
    
    // Use all provider listings to get all categories, not just the filtered ones
    const listingsToUse = allProviderListings && allProviderListings.length > 0 
      ? allProviderListings 
      : listings;
    
    const categorySet = new Set();
    
    listingsToUse.forEach(listing => {
      const listingObj = ensureListing(listing);
      const publicData = listingObj.attributes?.publicData || {};
      
      // Use getCategoryNames exactly like ManageListingCard does
      const categoryNames = getCategoryNames(
        publicData,
        categoryPrefix,
        categories
      );
      
      // Each listing has exactly 1 category - get the top-level category name
      if (categoryNames.length > 0) {
        categorySet.add(categoryNames[0]);
      }
    });
    
    // Return unique categories, limited to 8
    return Array.from(categorySet).slice(0, 8);
  }, [allProviderListings, listings, categoryPrefix, categories]);

  // Link to first listing or provider profile
  const linkTo = firstListing
    ? {
        name: 'ListingPage',
        params: {
          id: firstListing.id.uuid,
          slug: createSlug(firstListing.attributes.title),
        },
      }
    : providerId
    ? {
        name: 'ProfilePage',
        params: { id: providerId },
      }
    : null;

  return (
    <div className={classes}>
      <div className={css.imageContainer}>
        {linkTo ? (
          <NamedLink className={css.link} {...linkTo}>
            <AspectRatioWrapper
              className={css.aspectRatioWrapper}
              width={aspectWidth}
              height={aspectHeight}
            >
              <LazyImage
                rootClassName={css.rootForImage}
                alt={providerName || 'Provider'}
                image={displayImage}
                variants={variants}
                sizes={renderSizes}
              />
            </AspectRatioWrapper>
          </NamedLink>
        ) : (
          <AspectRatioWrapper
            className={css.aspectRatioWrapper}
            width={aspectWidth}
            height={aspectHeight}
          >
            <LazyImage
              rootClassName={css.rootForImage}
              alt={providerName || 'Provider'}
              image={displayImage}
              variants={variants}
              sizes={renderSizes}
            />
          </AspectRatioWrapper>
        )}
        <div className={css.imageOverlay}>
          {linkTo ? (
            <NamedLink className={css.providerNameLink} {...linkTo}>
              <div className={css.providerName}>{providerName}</div>
            </NamedLink>
          ) : (
            <div className={css.providerName}>{providerName}</div>
          )}
          <UserReviewsSummary
            reviews={userReviews}
            reviewType={REVIEW_TYPE_OF_PROVIDER}
            loading={reviewsLoading}
            className={css.userReviewsSummary}
          />
        </div>
      </div>
      {serviceCategories.length > 0 && (
        <div className={css.info}>
          <div className={css.servicesList}>
            <div className={css.servicesLabel}>
              <FormattedMessage id="ProviderCard.services" />
            </div>
            <div className={css.servicesTags}>
              {serviceCategories.map((categoryName, index) => (
                <span key={index} className={css.serviceTag}>
                  {categoryName}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderCard;
